<template>
  <div class="input_group">
    <v-autocomplete
      ref="autocompleteInput"
      v-model="selectedContact"
      :items="contactFilter.items.results"
      item-value="id"
      :filter="() => true"
      :label="needLabel ? $t('utils.contactSelectorLabel') : ''"
      :hide-no-data="!contactFilter.items.queryString || !!emailError"
      :search-input.sync="contactFilter.items.queryString"
      :loading="contactFilter.items.loading || isAdding"
      :error-messages="emailError"
      :menu-props="{ offsetY: true }"
      hide-selected
      dense
      small-chips
      outlined
      active
      v-bind="$attrs"
      @change="selectedContactUpdated()"
      @keypress.enter="addContact()"
    >
      <template #no-data>
        <div class="no_data_section">
          <div class="label" v-text="`${$t('utils.create')} : `" />
          <div class="value" v-text="contactFilter.items.queryString" />
          <BaseButton
            type="primary"
            small
            :loading="isAdding"
            @click.stop.prevent="addContact()"
          >
            {{ $t("utils.validate") }}
          </BaseButton>
        </div>
      </template>
      <template #selection="{ item }">
        <v-chip color="primary" label small>
          <span class="pr-2">
            {{ item.email }}
          </span>
          <v-icon small @click="deselectContact(item.id)">
            {{ "$mdiClose" }}
          </v-icon>
        </v-chip>
      </template>
      <template #item="{ item }">
        <div v-intersect="loadMoreClients" />

        <div class="contact_item">
          <div>
            {{ item.email }}
          </div>
        </div>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import ClientAPI from "@/modules/Client/Services/client.api";
export default {
  name: "OrganizationContactSelector",
  props: {
    value: {
      type: [Number, Array],
      default: null,
    },
    organizationId: {
      type: Number,
      required: true,
    },
    label: {
      type: String,
      default: "",
    },
    needLabel: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    selectedContact: null,
    emailError: "",
    isAdding: false,
    contactFilter: {
      items: {
        loading: false,
        page: 1,
        pageSize: 100,
        results: [],
        searchTimeoutRef: undefined,
        next: null,
        queryString: "",
      },
    },
  }),

  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.selectedContact = val;
      },
      deep: true,
    },
    "contactFilter.items.queryString": {
      handler() {
        this.handleClientSearch();
        this.emailError = "";
      },
    },
  },

  methods: {
    // new
    async fetchClients() {
      if (
        this.contactFilter.items.loading &&
        !this.contactFilter.items.page &&
        !this.organizationId
      )
        return;
      this.contactFilter.items.loading = true;
      const res = await ClientAPI.getOrganizationClients(
        this.organizationId,
        this.contactFilter.items.page,
        this.contactFilter.items.pageSize,
        this.contactFilter.items.queryString
      );
      this.contactFilter.items = {
        ...this.contactFilter.items,
        results: [...this.contactFilter.items.results, ...res.data.results],
        loading: false,
        next: res.data.next,
      };
    },
    async loadMoreClients(_, __, isIntersecting) {
      if (
        !isIntersecting ||
        this.contactFilter.items.loading ||
        !this.contactFilter.items.next
      )
        return;
      try {
        this.contactFilter.items.page = this.contactFilter.items.next;
        await this.fetchClients();
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    handleClientSearch() {
      this.contactFilter.items.results = [];
      this.contactFilter.items.page = 1;
      if (this.contactFilter.items.searchTimeoutRef)
        clearTimeout(this.contactFilter.items.searchTimeoutRef);
      this.contactFilter.items.searchTimeoutRef = setTimeout(async () => {
        this.fetchClients();
      }, 500);
    },
    async addContact() {
      if (!this.validateEmail(this.contactFilter.items.queryString))
        return (this.emailError = this.$t(
          "folder.askInformationForm.validation.emailInvalid"
        ));
      this.isAdding = true;
      const newContact = (
        await ClientAPI.postOrganizationCreateClient(this.organizationId, {
          email: this.contactFilter.items.queryString.toLowerCase(),
        })
      ).data;
      this.contactFilter.items.queryString = "";
      await this.fetchClients();
      this.contactFilter.items.results.push(newContact);
      this.selectedContact = Array.isArray(this.selectedContact)
        ? [...this.selectedContact, newContact.id]
        : newContact.id;
      this.selectedContactUpdated();
      this.isAdding = false;
      this.$refs.autocompleteInput?.blur();
    },
    deselectContact(contactId) {
      if (this.$attrs.multiple) {
        this.selectedContact = this.selectedContact.filter(
          (id) => id !== contactId
        );
      } else {
        this.selectedContact = null;
      }
      this.contactFilter.items.queryString = "";
      this.selectedContactUpdated();
    },
    selectedContactUpdated() {
      this.$emit("input", this.selectedContact ? this.selectedContact : null);
      this.emailError = "";
    },
    validateEmail(email) {
      if (!email) return false;
      return email.match(
        // eslint-disable-next-line no-useless-escape
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.input_group {
  .label {
    font-size: 14px;
    line-height: 18px;
    margin-bottom: 1em;
    color: #cfcfdc;
  }
  ::v-deep {
    .v-input__control .v-input__slot {
      & > fieldset {
        border: 1px solid #cfcfdc;
      }
    }
  }
}
.no_data_section {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 16px;
  .value {
    margin: 0 8px;
    flex-grow: 1;
  }
}
.contact_item {
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: space-between;
  align-items: center;
}
.menu_list {
  background: #fff;
  .menu_list_item {
    display: flex;
    justify-content: center;
    &::v-deep {
      .label {
        font-size: 12px;
      }
    }
  }
}

.v-select--is-menu-active {
  ::v-deep .v-input__slot > fieldset {
    border: 2px solid #2536cc !important;
  }
}
</style>
